import React, { useState, useEffect } from 'react';
import { Link as LinkIcon, FileText, RefreshCw, ExternalLink, Search } from 'lucide-react';
import * as figmaService from '../../services/figmaService';
import { FigmaInstance, FigmaFile } from '../../services/figmaService';

interface FigmaFileSyncProps {
  clientId: number;
  projectId: number;
  token: string;
  onFileLinked?: (file: FigmaFile) => void;
}

const FigmaFileSync: React.FC<FigmaFileSyncProps> = ({ clientId, projectId, token, onFileLinked }) => {
  const [instances, setInstances] = useState<FigmaInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<number | null>(null);
  const [files, setFiles] = useState<FigmaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [showSyncForm, setShowSyncForm] = useState(false);

  useEffect(() => {
    fetchInstances();
  }, [clientId]);

  useEffect(() => {
    if (selectedInstance) {
      fetchFiles(selectedInstance);
    }
  }, [selectedInstance]);

  const fetchInstances = async () => {
    try {
      const data = await figmaService.getFigmaInstances(clientId, token);
      setInstances(data);
      if (data.length > 0 && !selectedInstance) {
        setSelectedInstance(data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch Figma instances:', error);
    }
  };

  const fetchFiles = async (instanceId: number) => {
    try {
      setLoading(true);
      const data = await figmaService.getFigmaFiles(instanceId, token);
      setFiles(data);
    } catch (error) {
      console.error('Failed to fetch Figma files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedInstance) {
      alert('Please select a Figma instance');
      return;
    }

    const fileKey = figmaService.extractFileKeyFromUrl(figmaUrl);
    if (!fileKey) {
      alert('Invalid Figma URL. Please enter a valid Figma file URL.');
      return;
    }

    console.log('FigmaFileSync - syncing with:', { instanceId: selectedInstance, fileKey, projectId });

    try {
      setSyncing(true);
      const result = await figmaService.syncFigmaFile(
        selectedInstance,
        { fileKey, projectId },
        token
      );

      alert(`File synced successfully!\n${result.fileName}\n${result.versionsCount} versions, ${result.commentsCount} comments`);
      setFigmaUrl('');
      setShowSyncForm(false);
      fetchFiles(selectedInstance);
    } catch (error: any) {
      console.error('Failed to sync Figma file:', error);
      alert(error.response?.data?.details || 'Failed to sync Figma file');
    } finally {
      setSyncing(false);
    }
  };

  if (instances.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LinkIcon className="w-8 h-8 text-purple-600" />
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Figma Connection</h4>
          <p className="text-gray-600">
            Please connect your Figma account in the client settings first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Figma Files</h3>
        <button
          onClick={() => setShowSyncForm(!showSyncForm)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
        >
          <LinkIcon className="w-4 h-4" />
          Link Figma File
        </button>
      </div>

      {/* Instance Selector */}
      {instances.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Figma Account
          </label>
          <select
            value={selectedInstance || ''}
            onChange={(e) => setSelectedInstance(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {instances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.instance_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Sync Form */}
      {showSyncForm && (
        <form onSubmit={handleSyncFile} className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <h4 className="font-medium text-gray-900 mb-4">Sync Figma File</h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Figma File URL
              </label>
              <input
                type="text"
                value={figmaUrl}
                onChange={(e) => setFigmaUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="https://www.figma.com/file/..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Paste the URL of the Figma file you want to sync
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowSyncForm(false);
                  setFigmaUrl('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={syncing}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                    Syncing...
                  </>
                ) : (
                  'Sync File'
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Files List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No Figma files linked to this project yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors group"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">F</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-medium text-gray-900 truncate">{file.file_name}</h5>
                  <p className="text-xs text-gray-500">
                    {file.last_modified && `Updated ${new Date(file.last_modified).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onFileLinked?.(file)}
                  className="px-3 py-1.5 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                >
                  Use This File
                </button>
                <a
                  href={figmaService.getFigmaFileUrl(file.file_key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Open in Figma"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FigmaFileSync;
